import { useCallback } from 'react';
import { messages, LocaleKey } from '../locales';
import { SUPPORTED_LANGUAGES } from '../types/settings';

/**
 * i18n Hook - 多语言支持
 */
export const useI18n = () => {
  // 获取当前语言
  const getCurrentLanguage = useCallback((): string => {
    try {
      const stored = localStorage.getItem('user-language');
      if (stored && SUPPORTED_LANGUAGES.some(lang => lang.code === stored)) {
        return stored;
      }
    } catch (error) {
      console.warn('Failed to get language from localStorage:', error);
    }

    // 从浏览器获取默认语言
    try {
      const browserLang = navigator.language;
      const supportedCodes = SUPPORTED_LANGUAGES.map(lang => lang.code);
      if (supportedCodes.includes(browserLang)) {
        return browserLang;
      }

      // 检查语言前缀匹配（如 zh-CN 匹配 zh）
      const prefix = browserLang.split('-')[0];
      const matchedLang = SUPPORTED_LANGUAGES.find(lang =>
        lang.code.startsWith(prefix)
      );
      if (matchedLang) {
        return matchedLang.code;
      }
    } catch (error) {
      console.warn('Failed to get browser language:', error);
    }

    return 'zh-CN'; // 默认中文
  }, []);

  // 设置当前语言
  const setCurrentLanguage = useCallback((language: string) => {
    try {
      localStorage.setItem('user-language', language);
      // 触发自定义事件通知其他组件语言已改变
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: language }));
    } catch (error) {
      console.error('Failed to save language to localStorage:', error);
    }
  }, []);

  // 获取翻译文本
  const t = useCallback((key: string, params?: Record<string, any>): string => {
    const currentLang = getCurrentLanguage() as LocaleKey;

    const keys = key.split('.');
    let value: any = messages[currentLang];

    // 遍历嵌套的键
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }

    // 如果当前语言没有找到翻译，尝试英文
    if (value === undefined && currentLang !== 'en-US') {
      value = messages['en-US'];
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          value = undefined;
          break;
        }
      }
    }

    // 如果仍然没有找到，返回键名
    if (typeof value !== 'string') {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }

    // 替换参数
    if (params) {
      return Object.keys(params).reduce((str, param) => {
        return str.replace(new RegExp(`{{${param}}}`, 'g'), String(params[param]));
      }, value);
    }

    return value;
  }, [getCurrentLanguage]);

  return {
    t,
    currentLanguage: getCurrentLanguage(),
    setCurrentLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
};
